import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { CaretLeft, CaretRight, MapPin, Clock, CalendarBlank, MagnifyingGlass } from '@phosphor-icons/react';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  addDays,
  addWeeks,
  subWeeks,
  addMonths,
  subMonths,
  isSameDay,
  parseISO,
  isValid,
} from 'date-fns';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { API } from '../config';
import { orderMatchesGlobalSearch } from '../utils/listSearch';
import {
  getPlanningOrderViewPath,
  isOrderAwaitingServicePlanning,
  isOrderInActiveServiceQueue,
  isOrderOnPlanningSidebar,
} from '../utils/orderAttention';

// Custom portal for dragging - this fixes the alignment issue
const PortalAwareItem = ({ provided, snapshot, children }) => {
  const usePortal = snapshot.isDragging;
  
  const child = (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      style={provided.draggableProps.style}
    >
      {children}
    </div>
  );

  if (!usePortal) {
    return child;
  }

  return createPortal(child, document.body);
};

const parsePlanningDragOrderId = (draggableId) => {
  const raw = String(draggableId);
  return raw.includes('__') ? raw.split('__')[0] : raw;
};

const planningDragId = (orderId, day) =>
  `${orderId}__${format(day, 'yyyy-MM-dd')}`;

const PlanningPage = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentWeek, setCurrentWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [viewMode, setViewMode] = useState('week');
  const [allOrders, setAllOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarWorkDays, setSidebarWorkDays] = useState({});

  const sidebarOrders = useMemo(
    () => allOrders.filter((o) => isOrderOnPlanningSidebar(o)),
    [allOrders],
  );
  const unassignedSidebarOrders = useMemo(
    () => sidebarOrders.filter((o) => o.status === 'unassigned'),
    [sidebarOrders],
  );
  const serviceSidebarOrders = useMemo(
    () => sidebarOrders.filter((o) => isOrderAwaitingServicePlanning(o)),
    [sidebarOrders],
  );

  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(currentWeek, i)), [currentWeek]);
  const monthDays = useMemo(
    () => eachDayOfInterval({ start: startOfMonth(currentWeek), end: endOfMonth(currentWeek) }),
    [currentWeek]
  );
  const monthGridDays = useMemo(
    () =>
      eachDayOfInterval({
        start: startOfWeek(startOfMonth(currentWeek), { weekStartsOn: 1 }),
        end: endOfWeek(endOfMonth(currentWeek), { weekStartsOn: 1 }),
      }),
    [currentWeek]
  );
  const visibleDays = viewMode === 'month' ? monthDays : weekDays;
  const periodStart = viewMode === 'month' ? startOfMonth(currentWeek) : currentWeek;
  const periodEnd = viewMode === 'month' ? endOfMonth(currentWeek) : endOfWeek(currentWeek, { weekStartsOn: 1 });
  const dayKeys = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
  const markerStyles = {
    red: { bg: 'rgba(239, 68, 68, 0.16)', border: '#ef4444' },
    yellow: { bg: 'rgba(245, 158, 11, 0.2)', border: '#f59e0b' },
    green: { bg: 'rgba(16, 185, 129, 0.18)', border: '#10b981' },
  };

  const isWeekend = (d) => {
    const n = d.getDay();
    return n === 0 || n === 6;
  };

  const addWorkingDays = (startDate, daysToInclude) => {
    if (!isValid(startDate)) return startDate;
    let cursor = startDate;
    if (isWeekend(cursor)) {
      while (isWeekend(cursor)) cursor = addDays(cursor, 1);
    }
    let remaining = Math.max(daysToInclude, 1) - 1;
    while (remaining > 0) {
      cursor = addDays(cursor, 1);
      if (isWeekend(cursor)) continue;
      remaining -= 1;
    }
    return cursor;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getOrderActiveRange = (order) => {
    const startRaw = order.scheduled_date || order.montagedatum;
    if (!startRaw) return null;
    let start = parseISO(startRaw);
    if (!isValid(start)) return null;
    if (isWeekend(start)) {
      while (isWeekend(start)) start = addDays(start, 1);
    }
    const totalDays = Math.max(Number(order.montagedagen || 0), 1);
    const plannedEnd = addWorkingDays(start, totalDays);
    const completedRaw =
      order.completed_at ||
      order.mobile_completed_at ||
      order.client_signature_json?.signed_at ||
      null;
    const completedAt = completedRaw ? parseISO(String(completedRaw)) : null;
    const today = parseISO(format(new Date(), 'yyyy-MM-dd'));
    const end = order.mobile_work_status === 'completed'
      ? (
          (completedAt && isValid(completedAt) && completedAt < plannedEnd)
            ? completedAt
            : today < plannedEnd
              ? today
              : plannedEnd
        )
      : plannedEnd;
    return { start, end };
  };

  const plannedOrders = useMemo(() => {
    const periodStartDay = parseISO(format(periodStart, 'yyyy-MM-dd'));
    const periodEndDay = parseISO(format(periodEnd, 'yyyy-MM-dd'));
    if (!isValid(periodStartDay) || !isValid(periodEndDay)) return [];

    return allOrders.filter((o) => {
      if (String(o.status || '') !== 'assigned') return false;
      if (String(o.mobile_work_status || '') === 'completed') return false;
      const range = getOrderActiveRange(o);
      if (!range) return false;
      return range.start <= periodEndDay && range.end >= periodStartDay;
    });
  }, [allOrders, periodStart, periodEnd]);

  useEffect(() => {
    setSidebarWorkDays((prev) => {
      const next = { ...prev };
      for (const order of allOrders) {
        if (!order.scheduled_date && !order.montagedatum) continue;
        const id = String(order.id);
        if (next[id] == null) {
          next[id] = Math.max(Number(order.montagedagen || 0), 1);
        }
      }
      for (const order of sidebarOrders) {
        const id = String(order.id);
        if (next[id] == null) {
          next[id] = Math.max(Number(order.montagedagen || 0), 1);
        }
      }
      return next;
    });
  }, [allOrders, sidebarOrders]);

  const getSidebarWorkDays = (orderId) => {
    const id = String(orderId);
    const value = sidebarWorkDays[id];
    return value != null && value >= 1 ? value : 1;
  };

  const setSidebarWorkDaysForOrder = (orderId, rawValue) => {
    const parsed = Math.max(parseInt(String(rawValue), 10) || 1, 1);
    setSidebarWorkDays((prev) => ({ ...prev, [String(orderId)]: parsed }));
  };

  const fetchData = async () => {
    try {
      const [teamsRes, allOrdersRes] = await Promise.all([
        axios.get(`${API}/teams`, { withCredentials: true }),
        axios.get(`${API}/orders`, { withCredentials: true }),
      ]);

      setTeams(teamsRes.data);
      setAllOrders(Array.isArray(allOrdersRes.data) ? allOrdersRes.data : []);
    } catch (error) {
      toast.error(t('failedFetchData'));
    } finally {
      setLoading(false);
    }
  };

  const getOrdersForDay = (date) => {
    const dayStart = parseISO(format(date, 'yyyy-MM-dd'));
    if (isWeekend(dayStart)) return [];
    return plannedOrders.filter((order) => {
      const range = getOrderActiveRange(order);
      if (!range) return false;
      if (activeSearchIds && !activeSearchIds.has(String(order.id))) return false;
      return dayStart >= range.start && dayStart <= range.end;
    });
  };

  const getOrderMarkerStyle = (order) => {
    const key = String(order?.planning_marker_color || '').trim().toLowerCase();
    return markerStyles[key] || null;
  };

  const countWorkingDays = (startDate, endDate) => {
    if (!isValid(startDate) || !isValid(endDate) || endDate < startDate) return 0;
    let count = 0;
    for (let d = startDate; d <= endDate; d = addDays(d, 1)) {
      if (!isWeekend(d)) count += 1;
    }
    return count;
  };

  const searchNeedle = searchQuery.trim().toLowerCase();
  const searchMatches = useMemo(() => {
    if (!searchNeedle) return [];
    return plannedOrders
      .map((order) => {
        if (!orderMatchesGlobalSearch(order, searchNeedle)) return null;
        const range = getOrderActiveRange(order);
        if (!range) return null;
        const overlapStart = range.start > periodStart ? range.start : periodStart;
        const overlapEnd = range.end < periodEnd ? range.end : periodEnd;
        const workDays = countWorkingDays(overlapStart, overlapEnd);
        if (workDays <= 0) return null;
        return { order, workDays };
      })
      .filter(Boolean);
  }, [plannedOrders, searchNeedle, periodStart, periodEnd]);

  const activeSearchIds = useMemo(() => {
    if (!searchNeedle) return null;
    return new Set(searchMatches.map((m) => String(m.order.id)));
  }, [searchNeedle, searchMatches]);

  const planningOrdersTotal = useMemo(() => {
    const seen = new Set();
    let sum = 0;
    let missing = 0;
    for (const o of plannedOrders) {
      const id = String(o.id ?? '');
      if (!id || seen.has(id)) continue;
      seen.add(id);
      const raw = o.offer_total_amount;
      if (raw == null || raw === '') {
        missing += 1;
        continue;
      }
      const n = Number(raw);
      if (!Number.isFinite(n)) missing += 1;
      else sum += n;
    }
    return { sum, missing };
  }, [plannedOrders]);

  const formatPlanningTotal = (value) =>
    new Intl.NumberFormat(language === 'nl' ? 'nl-NL' : 'en-GB', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);

  const handleDragEnd = async (result) => {
    const { source, destination, draggableId } = result;

    if (!destination) return;
    if (source.droppableId === destination.droppableId) return;

    const orderId = parsePlanningDragOrderId(draggableId);
    const draggedOrder = allOrders.find((o) => String(o.id) === String(orderId));

    if (destination.droppableId === 'unassigned') {
      try {
        if (draggedOrder && isOrderInActiveServiceQueue(draggedOrder)) {
          await axios.put(`${API}/orders/${orderId}/clear-planning`, {}, { withCredentials: true });
          toast.success(t('planningServiceClearedForReschedule'));
        } else {
          await axios.put(`${API}/orders/${orderId}/unassign`, {}, { withCredentials: true });
          toast.success(t('orderUnassignedSuccess'));
        }
        fetchData();
      } catch (error) {
        toast.error(t('failedUnassignOrder'));
      }
      return;
    }

    const parts = destination.droppableId.split('-team-');
    const dateStr = parts[0].replace('day-', '');
    let teamId = parts[1];

    if (!teamId) {
      toast.error(t('planningDragTeamRequired'));
      return;
    }

    if (teamId === 'self') {
      if (!draggedOrder?.assigned_team_id) {
        toast.error(t('planningDragTeamRequired'));
        return;
      }
      teamId = String(draggedOrder.assigned_team_id);
    }

    const fromSidebar = source.droppableId === 'unassigned';
    const payload = { team_id: teamId, scheduled_date: dateStr };
    if (fromSidebar) {
      payload.montagedagen = getSidebarWorkDays(orderId);
    } else if (draggedOrder) {
      payload.montagedagen = Math.max(Number(draggedOrder.montagedagen || 0), 1);
    }

    try {
      await axios.put(
        `${API}/orders/${orderId}/assign`,
        payload,
        { withCredentials: true }
      );
      toast.success(t('orderAssignedSuccess'));
      fetchData();
    } catch (error) {
      toast.error(t('failedAssignOrder'));
    }
  };

  const prevWeek = () => setCurrentWeek(subWeeks(currentWeek, 1));
  const nextWeek = () => setCurrentWeek(addWeeks(currentWeek, 1));
  const prevMonth = () => setCurrentWeek(startOfWeek(subMonths(currentWeek, 1), { weekStartsOn: 1 }));
  const nextMonth = () => setCurrentWeek(startOfWeek(addMonths(currentWeek, 1), { weekStartsOn: 1 }));
  const goToToday = () => setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#00CBA9] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div data-testid="planning-page" className="animate-fadeIn h-full">
      {/* Header */}
      <div className="mb-6 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1fr_minmax(360px,520px)_1fr]">
        <div>
          <h1 className="page-title">{t('planning')}</h1>
          <p className="page-subtitle">
            {viewMode === 'month'
              ? `${format(startOfMonth(currentWeek), 'MMMM yyyy')}`
              : `${t('week')} ${format(currentWeek, 'w')} • ${format(currentWeek, 'MMMM yyyy')}`}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-[#64748b] dark:text-[#94a3b8]">
              {viewMode === 'month' ? t('planningMonthTotalLabel') : t('planningWeekTotalLabel')}
            </span>
            <span className="inline-flex items-center rounded-full border border-[#00CBA9]/30 bg-gradient-to-r from-[#00CBA9]/20 to-[#34d399]/20 px-3 py-1 text-sm font-bold text-[#047857] shadow-sm dark:border-[#00CBA9]/40 dark:from-[#00CBA9]/25 dark:to-[#34d399]/25 dark:text-[#6ee7b7]">
              {formatPlanningTotal(planningOrdersTotal.sum)}
            </span>
            {planningOrdersTotal.missing > 0 ? (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                {t('planningTotalExcludesMissing')}
              </span>
            ) : null}
          </div>
        </div>
        <div className="w-full justify-self-center lg:self-center">
          <div className="relative">
            <MagnifyingGlass
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('planningSearchPlaceholder')}
              className="vmk-input !pl-9"
            />
          </div>
          {searchNeedle ? (
            <div className="mt-2 flex flex-wrap justify-center gap-2">
              {searchMatches.length > 0 ? (
                searchMatches.map(({ order, workDays }) => (
                  <span
                    key={order.id}
                    className="inline-flex items-center rounded-full border border-[#00CBA9]/30 bg-[#00CBA9]/10 px-2.5 py-1 text-xs text-[#0f766e] dark:border-[#00CBA9]/40 dark:bg-[#00CBA9]/15 dark:text-[#99f6e4]"
                  >
                    {order.client_name} - {workDays} {workDays === 1 ? t('planningWorkday') : t('planningWorkdays')}
                  </span>
                ))
              ) : (
                <span className="text-xs text-[#94a3b8]">{t('planningSearchNoResults')}</span>
              )}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-2 lg:justify-self-end">
          <button
            onClick={viewMode === 'month' ? prevMonth : prevWeek}
            className="vmk-btn p-3"
            data-testid="prev-week-btn"
          >
            <CaretLeft size={18} weight="bold" />
          </button>
          <button
            onClick={goToToday}
            className="vmk-btn px-5"
            data-testid="today-btn"
          >
            <CalendarBlank size={18} />
            {t('today')}
          </button>
          <button
            onClick={viewMode === 'month' ? nextMonth : nextWeek}
            className="vmk-btn p-3"
            data-testid="next-week-btn"
          >
            <CaretRight size={18} weight="bold" />
          </button>
          <button
            onClick={() => setViewMode((prev) => (prev === 'week' ? 'month' : 'week'))}
            className="vmk-btn px-5"
            data-testid="toggle-view-btn"
          >
            {viewMode === 'week' ? t('month') : t('week')}
          </button>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 h-[calc(100vh-180px)]">
          {/* Unassigned + open service orders awaiting schedule */}
          <div className="w-64 flex-shrink-0">
            <div className="vmk-card-flat h-full flex flex-col">
              <div className="px-4 py-3 border-b border-[#e2e8f0] dark:border-[#334155] bg-[#f8f9fb] dark:bg-[#1e293b]">
                <h3 className="text-sm font-semibold text-[#1a1a1a] dark:text-white flex items-center gap-2">
                  <Clock size={16} className="text-[#00CBA9]" />
                  {t('unassigned')}
                  <span className="ml-auto bg-[#00CBA9] text-white text-xs px-2 py-0.5 rounded-full">
                    {sidebarOrders.length}
                  </span>
                </h3>
              </div>
              <Droppable droppableId="unassigned">
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-3 flex-1 overflow-y-auto transition-colors ${
                      snapshot.isDraggingOver ? 'bg-[#00CBA9]/5' : ''
                    }`}
                    data-testid="unassigned-orders"
                  >
                    {sidebarOrders.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-sm text-[#94a3b8]">{t('planningNoSidebarOrders')}</p>
                      </div>
                    ) : (
                      <>
                        {unassignedSidebarOrders.length > 0 ? (
                          <div className="mb-3">
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                              {t('unassigned')}
                            </p>
                            {unassignedSidebarOrders.map((order, index) => (
                              <Draggable key={order.id} draggableId={String(order.id)} index={index}>
                                {(provided, snapshot) => (
                                  <PortalAwareItem provided={provided} snapshot={snapshot}>
                                    <div
                                      className={`order-card mb-2 ${snapshot.isDragging ? 'vmk-dragging' : ''}`}
                                      data-testid={`unassigned-order-${order.id}`}
                                    >
                                      <h4 className="font-semibold text-sm text-[#1a1a1a] dark:text-white">{order.client_name}</h4>
                                      <p className="text-xs text-[#64748b] flex items-center gap-1 mt-1">
                                        <MapPin size={12} className="text-[#00CBA9]" />
                                        {order.address}
                                      </p>
                                      {order.kozijnen ? (
                                        <p className="text-xs font-mono text-[#94a3b8] mt-2 bg-[#f8f9fb] dark:bg-[#1e293b] px-2 py-1 rounded">
                                          {order.kozijnen}
                                        </p>
                                      ) : null}
                                      <label
                                        className="mt-2 flex items-center justify-between gap-2 text-[10px] text-[#64748b]"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                      >
                                        <span>{t('planningSidebarDays')}</span>
                                        <input
                                          type="number"
                                          min="1"
                                          value={getSidebarWorkDays(order.id)}
                                          onChange={(e) => setSidebarWorkDaysForOrder(order.id, e.target.value)}
                                          className="vmk-input !w-14 !py-1 !text-xs"
                                          data-testid={`planning-days-${order.id}`}
                                        />
                                      </label>
                                    </div>
                                  </PortalAwareItem>
                                )}
                              </Draggable>
                            ))}
                          </div>
                        ) : null}
                        {serviceSidebarOrders.length > 0 ? (
                          <div>
                            <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-[#94a3b8]">
                              {t('planningServiceQueue')}
                            </p>
                            {serviceSidebarOrders.map((order, index) => (
                              <Draggable
                                key={`service-${order.id}`}
                                draggableId={String(order.id)}
                                index={unassignedSidebarOrders.length + index}
                              >
                                {(provided, snapshot) => (
                                  <PortalAwareItem provided={provided} snapshot={snapshot}>
                                    <div
                                      className={`order-card mb-2 border-l-[3px] border-l-amber-500 ${snapshot.isDragging ? 'vmk-dragging' : ''}`}
                                      data-testid={`service-planning-order-${order.id}`}
                                    >
                                      <div className="flex items-start justify-between gap-2">
                                        <h4 className="font-semibold text-sm text-[#1a1a1a] dark:text-white">{order.client_name}</h4>
                                        <span className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                          {t('planningServiceQueue')}
                                        </span>
                                      </div>
                                      <p className="text-xs text-[#64748b] flex items-center gap-1 mt-1">
                                        <MapPin size={12} className="text-[#00CBA9]" />
                                        {order.address}
                                      </p>
                                      <p className="mt-1 text-[10px] text-amber-700 dark:text-amber-300">
                                        {t('planningServiceAwaiting')}
                                      </p>
                                      <label
                                        className="mt-2 flex items-center justify-between gap-2 text-[10px] text-[#64748b]"
                                        onMouseDown={(e) => e.stopPropagation()}
                                        onTouchStart={(e) => e.stopPropagation()}
                                      >
                                        <span>{t('planningSidebarDays')}</span>
                                        <input
                                          type="number"
                                          min="1"
                                          value={getSidebarWorkDays(order.id)}
                                          onChange={(e) => setSidebarWorkDaysForOrder(order.id, e.target.value)}
                                          className="vmk-input !w-14 !py-1 !text-xs"
                                          data-testid={`planning-service-days-${order.id}`}
                                        />
                                      </label>
                                    </div>
                                  </PortalAwareItem>
                                )}
                              </Draggable>
                            ))}
                          </div>
                        ) : null}
                      </>
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          </div>

          {/* Calendar Grid - Full Width */}
          <div className="flex-1 overflow-hidden">
            {viewMode === 'month' ? (
              <div className="h-full flex flex-col">
                <div className="grid grid-cols-7 gap-2 mb-2">
                  {dayKeys.map((k) => (
                    <div key={k} className="text-xs font-semibold text-[#94a3b8] uppercase text-center py-1">
                      {t(k)}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-2 flex-1 auto-rows-fr overflow-y-auto">
                  {monthGridDays.map((day) => {
                    const inCurrentMonth = day.getMonth() === startOfMonth(currentWeek).getMonth();
                    const isToday = isSameDay(day, new Date());
                    const dayOrders = getOrdersForDay(day);
                    return (
                      <div
                        key={format(day, 'yyyy-MM-dd')}
                        className={`rounded-xl border p-2 min-h-[130px] flex flex-col ${
                          inCurrentMonth
                            ? 'bg-white dark:bg-[#1e293b] border-[#e2e8f0] dark:border-[#334155]'
                            : 'bg-[#f8fafc] dark:bg-[#0f172a] border-[#e2e8f0] dark:border-[#1e293b] opacity-70'
                        }`}
                        data-testid={`month-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span
                            className={`text-sm font-bold ${
                              isToday ? 'text-[#00CBA9]' : 'text-[#1a1a1a] dark:text-white'
                            }`}
                          >
                            {format(day, 'd')}
                          </span>
                          <span className="text-[10px] text-[#94a3b8]">{dayOrders.length} {t('orders').toLowerCase()}</span>
                        </div>
                        <Droppable
                          droppableId={
                            isWeekend(day)
                              ? `month-weekend-${format(day, 'yyyy-MM-dd')}`
                              : `day-${format(day, 'yyyy-MM-dd')}-team-self`
                          }
                          isDropDisabled={isWeekend(day)}
                        >
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.droppableProps}
                              className={`min-h-[72px] flex-1 space-y-1 overflow-y-auto rounded-md transition-colors ${
                                snapshot.isDraggingOver && !isWeekend(day) ? 'bg-[#00CBA9]/10' : ''
                              }`}
                            >
                              {dayOrders.slice(0, 4).map((order, index) => {
                                const team = teams.find((tm) => tm.id === order.assigned_team_id);
                                return (
                                  <Draggable
                                    key={planningDragId(order.id, day)}
                                    draggableId={planningDragId(order.id, day)}
                                    index={index}
                                  >
                                    {(dragProvided, dragSnapshot) => {
                                      const card = (
                                        <div
                                          ref={dragProvided.innerRef}
                                          {...dragProvided.draggableProps}
                                          style={dragProvided.draggableProps.style}
                                          className={`flex gap-1 ${dragSnapshot.isDragging ? 'opacity-90' : ''}`}
                                        >
                                          <div
                                            {...dragProvided.dragHandleProps}
                                            className="flex w-4 flex-shrink-0 cursor-grab items-center justify-center text-[#94a3b8]"
                                            aria-label="Drag to move"
                                          >
                                            ⋮
                                          </div>
                                          <button
                                            type="button"
                                            className="min-w-0 flex-1 text-left rounded-md border border-[#e2e8f0] dark:border-[#334155] px-2 py-1 bg-[#f8f9fb] dark:bg-[#0f172a] hover:brightness-[0.98] dark:hover:brightness-110 transition-[filter]"
                                            style={{
                                              backgroundColor: getOrderMarkerStyle(order)?.bg,
                                              borderLeftWidth: '3px',
                                              borderLeftColor:
                                                getOrderMarkerStyle(order)?.border || team?.color || '#00CBA9',
                                            }}
                                            onClick={() => navigate(getPlanningOrderViewPath(order))}
                                          >
                                            <div className="text-[11px] font-semibold truncate">{order.client_name}</div>
                                            <div className="text-[10px] text-[#64748b] truncate flex items-center gap-1">
                                              <span
                                                className="inline-block w-2 h-2 rounded-full flex-shrink-0"
                                                style={{ backgroundColor: team?.color || '#00CBA9' }}
                                              />
                                              <span className="truncate">
                                                {team?.name || t('team')} - {order.address}
                                              </span>
                                            </div>
                                          </button>
                                        </div>
                                      );
                                      return dragSnapshot.isDragging ? createPortal(card, document.body) : card;
                                    }}
                                  </Draggable>
                                );
                              })}
                              {dayOrders.length > 4 && (
                                <div className="text-[10px] text-[#64748b] px-1">
                                  +{dayOrders.length - 4} {t('more')}
                                </div>
                              )}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="h-full">
                <div className="grid grid-cols-7 gap-2 h-full w-full">
              {visibleDays.map((day, dayIndex) => {
                const isToday = isSameDay(day, new Date());
                const dayOrders = getOrdersForDay(day);
                const dayName = dayKeys[dayIndex];

                return (
                  <div 
                    key={dayIndex} 
                    className="planning-column flex flex-col h-full min-w-0" 
                    data-testid={`day-column-${format(day, 'yyyy-MM-dd')}`}
                  >
                    <div className={`planning-column-header flex-shrink-0 ${isToday ? 'today' : ''}`}>
                      <div className={`text-xs font-medium ${isToday ? 'text-white/70' : 'text-[#94a3b8]'}`}>
                        {t(dayName) || format(day, 'EEE')}
                      </div>
                      <div className={`text-xl font-bold ${isToday ? 'text-white' : 'text-[#1a1a1a] dark:text-white'}`}>
                        {format(day, 'd')}
                      </div>
                      <div className={`text-xs ${isToday ? 'text-white/70' : 'text-[#94a3b8]'}`}>
                        {format(day, 'MMM')}
                      </div>
                    </div>
                    
                    {teams.length === 0 ? (
                      <div className="p-2 flex-1 text-center flex items-center justify-center">
                        <p className="text-xs text-[#94a3b8]">{t('addTeamsFirst')}</p>
                      </div>
                    ) : (
                      <div className="flex-1 overflow-y-auto divide-y divide-[#f1f5f9] dark:divide-[#1e293b]">
                        {teams.map((team) => {
                          const teamOrders = dayOrders.filter(o => o.assigned_team_id === team.id);
                          const droppableId = `day-${format(day, 'yyyy-MM-dd')}-team-${team.id}`;

                          return (
                            <Droppable key={team.id} droppableId={droppableId}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.droppableProps}
                                  className={`p-2 min-h-[80px] transition-colors ${
                                    snapshot.isDraggingOver ? 'bg-[#00CBA9]/10' : ''
                                  }`}
                                  data-testid={`drop-zone-${format(day, 'yyyy-MM-dd')}-${team.id}`}
                                >
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <div
                                      className="w-2 h-2 rounded-full flex-shrink-0"
                                      style={{ backgroundColor: team.color }}
                                    />
                                    <span className="text-[10px] font-semibold text-[#64748b] uppercase truncate">
                                      {team.name}
                                    </span>
                                  </div>
                                  {teamOrders.map((order, index) => (
                                    <Draggable
                                      key={planningDragId(order.id, day)}
                                      draggableId={planningDragId(order.id, day)}
                                      index={index}
                                    >
                                      {(provided, snapshot) => {
                                        const row = (
                                          <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            style={provided.draggableProps.style}
                                            className={`mb-1.5 flex gap-1 ${
                                              snapshot.isDragging ? 'opacity-90' : ''
                                            }`}
                                            data-testid={`planned-order-${order.id}`}
                                          >
                                            <div
                                              {...provided.dragHandleProps}
                                              className="flex w-6 flex-shrink-0 cursor-grab items-center justify-center rounded-md border border-[#e2e8f0] bg-[#f8fafc] dark:border-[#334155] dark:bg-[#0f172a]"
                                              aria-label="Drag to move"
                                            >
                                              <span className="select-none text-[10px] leading-none text-[#94a3b8]">
                                                ⋮⋮
                                              </span>
                                            </div>
                                            <button
                                              type="button"
                                              className={`min-w-0 flex-1 cursor-pointer rounded-lg border border-[#e2e8f0] bg-white p-2 text-left dark:border-[#334155] dark:bg-[#1e293b] ${
                                                snapshot.isDragging
                                                  ? 'shadow-lg ring-2 ring-[#00CBA9]'
                                                  : ''
                                              }`}
                                              style={{
                                                backgroundColor: getOrderMarkerStyle(order)?.bg,
                                                borderLeftWidth: '3px',
                                                borderLeftColor: getOrderMarkerStyle(order)?.border || team.color,
                                              }}
                                              onClick={() => navigate(getPlanningOrderViewPath(order))}
                                            >
                                              <h4 className="truncate text-[11px] font-semibold text-[#1a1a1a] dark:text-white">
                                                {order.client_name}
                                              </h4>
                                              <p className="mt-0.5 truncate text-[10px] text-[#94a3b8]">
                                                {order.address}
                                                {Number(order.montagedagen) > 1
                                                  ? ` · ${order.montagedagen} ${t('planningWorkdays')}`
                                                  : ''}
                                              </p>
                                            </button>
                                          </div>
                                        );
                                        return snapshot.isDragging
                                          ? createPortal(row, document.body)
                                          : row;
                                      }}
                                    </Draggable>
                                  ))}
                                  {provided.placeholder}
                                </div>
                              )}
                            </Droppable>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
                </div>
              </div>
            )}
          </div>
        </div>
      </DragDropContext>

      {teams.length === 0 && (
        <div className="mt-4 vmk-card bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-center">
          <p className="text-amber-700 dark:text-amber-400">
            {t('noTeamsConfigured')}
          </p>
        </div>
      )}
    </div>
  );
};

export default PlanningPage;
